import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useListAllMembershipPlans,
  useCreateMembershipPlan,
  useUpdateMembershipPlan,
  useDeleteMembershipPlan,
  getListAllMembershipPlansQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Crown, Plus, Pencil, Trash2, Check, X, Percent, Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface PlanForm {
  name: string;
  description: string;
  pricePerSession: string;
  privateCredits: string;
  discountPct: string;
}

const emptyForm: PlanForm = { name: "", description: "", pricePerSession: "", privateCredits: "", discountPct: "0" };

const computeMonthlyPrice = (pricePerSession: string, privateCredits: string): number => {
  const rate = parseFloat(pricePerSession);
  const sessions = parseInt(privateCredits, 10);
  if (!Number.isFinite(rate) || !Number.isFinite(sessions)) return 0;
  return Math.round(rate * sessions * 100) / 100;
};

export default function AdminMemberships() {
  const { user } = useAuth();
  const gymId = user?.gymId || "";
  const queryClient = useQueryClient();
  const { data: plans, isLoading } = useListAllMembershipPlans(gymId, { query: { enabled: !!gymId } });
  const { data: gym } = useQuery({
    queryKey: ["admin", "gym"],
    queryFn: async () => {
      const res = await fetch("/api/admin/gym", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch gym");
      return res.json();
    },
    enabled: !!gymId,
  });
  const createMutation = useCreateMembershipPlan();
  const updateMutation = useUpdateMembershipPlan();
  const deleteMutation = useDeleteMembershipPlan();
  const { toast } = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [thresholdInput, setThresholdInput] = useState<string>("");
  const [expiryWarningDaysInput, setExpiryWarningDaysInput] = useState<string>("7");
  const [unusedReminderDaysInput, setUnusedReminderDaysInput] = useState<string>("14");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (gym && !settingsLoaded) {
    setThresholdInput(String((gym as any).lowCreditThreshold ?? 1));
    setExpiryWarningDaysInput(String((gym as any).packageExpiryWarningDays ?? 7));
    setUnusedReminderDaysInput(String((gym as any).unusedPackageReminderDays ?? 14));
    setSettingsLoaded(true);
  }

  const saveThresholdMutation = useMutation({
    mutationFn: async (value: number) => {
      const res = await fetch("/api/admin/gym/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lowCreditThreshold: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to save threshold");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Alert threshold saved" });
      queryClient.invalidateQueries({ queryKey: ["admin", "gym"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const saveExpiryWarningMutation = useMutation({
    mutationFn: async (value: number) => {
      const res = await fetch("/api/admin/gym/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ packageExpiryWarningDays: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to save expiry warning setting");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Expiry warning window saved" });
      queryClient.invalidateQueries({ queryKey: ["admin", "gym"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleSaveThreshold = () => {
    const parsed = parseInt(thresholdInput, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast({ title: "Please enter a valid non-negative number", variant: "destructive" });
      return;
    }
    saveThresholdMutation.mutate(parsed);
  };

  const handleSaveExpiryWarning = () => {
    const parsed = parseInt(expiryWarningDaysInput, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 90) {
      toast({ title: "Please enter a number between 1 and 90", variant: "destructive" });
      return;
    }
    saveExpiryWarningMutation.mutate(parsed);
  };

  const saveUnusedReminderMutation = useMutation({
    mutationFn: async (value: number) => {
      const res = await fetch("/api/admin/gym/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ unusedPackageReminderDays: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to save unused reminder setting");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Unused package reminder saved" });
      queryClient.invalidateQueries({ queryKey: ["admin", "gym"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleSaveUnusedReminder = () => {
    const parsed = parseInt(unusedReminderDaysInput, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 180) {
      toast({ title: "Please enter a number between 1 and 180", variant: "destructive" });
      return;
    }
    saveUnusedReminderMutation.mutate(parsed);
  };

  const invalidatePlans = () => {
    queryClient.invalidateQueries({ queryKey: getListAllMembershipPlansQueryKey(gymId) });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (plan: any) => {
    const sessions = Number(plan.privateCredits) || 0;
    const monthly = Number(plan.priceMonthly) || 0;
    const derivedRate = sessions > 0 ? Math.round((monthly / sessions) * 100) / 100 : 0;
    setForm({
      name: plan.name ?? "",
      description: plan.description || "",
      pricePerSession: derivedRate > 0 ? String(derivedRate) : "",
      privateCredits: String(sessions),
      discountPct: String(plan.discountPct ?? 0),
    });
    setEditingId(plan.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    const sessions = parseInt(form.privateCredits, 10);
    const safeSessions = Number.isFinite(sessions) && sessions >= 0 ? sessions : 0;
    const computedMonthly = computeMonthlyPrice(form.pricePerSession, form.privateCredits);
    const discount = parseFloat(form.discountPct);
    const safeDiscount = Number.isFinite(discount) ? discount : 0;

    if (!form.name.trim()) {
      toast({ title: "Plan name is required", variant: "destructive" });
      return;
    }
    if (computedMonthly < 0 || !Number.isFinite(computedMonthly)) {
      toast({ title: "Monthly price must be a valid non-negative number", variant: "destructive" });
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        priceMonthly: computedMonthly,
        privateCredits: safeSessions,
        discountPct: safeDiscount,
      };

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: payload });
        toast({ title: "Plan updated" });
      } else {
        await createMutation.mutateAsync({ data: { ...payload, gymId } });
        toast({ title: "Plan created" });
      }
      resetForm();
      invalidatePlans();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ id });
      toast({ title: "Plan deactivated" });
      invalidatePlans();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleReactivate = async (id: string) => {
    setReactivatingId(id);
    try {
      await updateMutation.mutateAsync({ id, data: { isActive: true } });
      toast({ title: "Plan reactivated" });
      invalidatePlans();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setReactivatingId(null);
    }
  };

  const handlePermanentDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await fetch(`/api/memberships/plans/${id}/permanent`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to delete plan");
      }
      toast({ title: "Plan permanently deleted" });
      invalidatePlans();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const allPlans = (plans || []) as any[];
  const activePlans = allPlans
    .filter((p: any) => p.isActive)
    .sort((a: any, b: any) => {
      const costA = a.priceMonthly * (1 - (a.discountPct ?? 0) / 100);
      const costB = b.priceMonthly * (1 - (b.discountPct ?? 0) / 100);
      return costA - costB;
    });
  const inactivePlans = allPlans
    .filter((p: any) => !p.isActive)
    .sort((a: any, b: any) => {
      const costA = a.priceMonthly * (1 - (a.discountPct ?? 0) / 100);
      const costB = b.priceMonthly * (1 - (b.discountPct ?? 0) / 100);
      return costA - costB;
    });

  const previewMonthly = computeMonthlyPrice(form.pricePerSession, form.privateCredits);
  const previewDiscountPct = parseFloat(form.discountPct);

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Membership Plans</h1>
            <p className="text-muted-foreground text-lg">Create and manage membership tiers for your students</p>
          </div>
          {!showForm && (
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-primary text-black font-bold">
              <Plus className="w-4 h-4 mr-2" /> New Plan
            </Button>
          )}
        </div>

        <Card className="glass-panel border-amber-500/20 bg-amber-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Low Credit Alert Threshold</CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Students receive an email notification when their remaining credits reach this number or below
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 max-w-xs">
              <Input
                type="number"
                min="0"
                step="1"
                value={thresholdInput}
                onChange={e => setThresholdInput(e.target.value)}
                className="bg-white/5 border-white/10 text-white w-28"
                placeholder="1"
              />
              <span className="text-sm text-muted-foreground">credits remaining</span>
              <Button
                size="sm"
                onClick={handleSaveThreshold}
                disabled={saveThresholdMutation.isPending}
                className="bg-primary text-black font-bold ml-auto"
              >
                <Check className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Set to 0 to disable low-credit alerts. Default is 1 (alert when 1 credit remains).
            </p>
          </CardContent>
        </Card>

        <Card className="glass-panel border-orange-500/20 bg-orange-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-500/20 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Package Expiry Warning</CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Students with unused credits are notified this many days before their package expires
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 max-w-xs">
              <Input
                type="number"
                min="1"
                max="90"
                step="1"
                value={expiryWarningDaysInput}
                onChange={e => setExpiryWarningDaysInput(e.target.value)}
                className="bg-white/5 border-white/10 text-white w-28"
                placeholder="7"
              />
              <span className="text-sm text-muted-foreground">days before expiry</span>
              <Button
                size="sm"
                onClick={handleSaveExpiryWarning}
                disabled={saveExpiryWarningMutation.isPending}
                className="bg-primary text-black font-bold ml-auto"
              >
                <Check className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Only students with credits remaining receive this warning. Default is 7 days.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-panel border-blue-500/20 bg-blue-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Unused Package Nudge</CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Send a one-time reminder to students who have never used a package after this many days
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 max-w-xs">
              <Input
                type="number"
                min="1"
                max="180"
                step="1"
                value={unusedReminderDaysInput}
                onChange={e => setUnusedReminderDaysInput(e.target.value)}
                className="bg-white/5 border-white/10 text-white w-28"
                placeholder="14"
              />
              <span className="text-sm text-muted-foreground">days after purchase</span>
              <Button
                size="sm"
                onClick={handleSaveUnusedReminder}
                disabled={saveUnusedReminderMutation.isPending}
                className="bg-primary text-black font-bold ml-auto"
              >
                <Check className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Only fires once per package. Sent only while the package is still active. Default is 14 days.
            </p>
          </CardContent>
        </Card>

        {showForm && (
          <Card className="glass-panel border-primary/30">
            <CardHeader>
              <CardTitle className="text-xl text-white">{editingId ? "Edit Plan" : "Create New Plan"}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Plan Name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Gold Membership" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Price per Session ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.pricePerSession}
                    onChange={e => setForm({ ...form, pricePerSession: e.target.value })}
                    placeholder="100.00"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Private Sessions / Month</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.privateCredits}
                    onChange={e => setForm({ ...form, privateCredits: e.target.value })}
                    placeholder="4"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Monthly Price ($)</Label>
                  <Input
                    type="number"
                    value={previewMonthly > 0 ? previewMonthly.toFixed(2) : ""}
                    readOnly
                    placeholder="0.00"
                    className="bg-white/5 border-white/10 text-white/80 cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">Auto-calculated from sessions × rate</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Discount (%)</Label>
                  <Input type="number" min="0" max="100" step="1" value={form.discountPct} onChange={e => setForm({ ...form, discountPct: e.target.value })} placeholder="15" className="bg-white/5 border-white/10 text-white" />
                  <p className="text-xs text-muted-foreground">Applies to plan price and private session rates</p>
                </div>
              </div>
              {previewMonthly > 0 && Number.isFinite(previewDiscountPct) && previewDiscountPct > 0 && (
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 flex items-center gap-3">
                  <Percent className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div>
                    <p className="text-sm text-emerald-400 font-medium">
                      Discounted price: ${(previewMonthly * (1 - previewDiscountPct / 100)).toFixed(2)}/mo
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Original ${previewMonthly.toFixed(2)} with {previewDiscountPct}% off
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-white">Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description of this plan" className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending} className="bg-primary text-black font-bold">
                  <Check className="w-4 h-4 mr-2" /> {editingId ? "Update" : "Create"} Plan
                </Button>
                <Button variant="ghost" onClick={resetForm} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[1, 2, 3].map(i => <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />)}</div>
        ) : (
          <>
            {activePlans.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Active Plans</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {activePlans.map((plan: any) => (
                    <Card key={plan.id} className="glass-panel border-white/5 hover:-translate-y-1 transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                            <Crown className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-white" onClick={() => handleEdit(plan)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-400" onClick={() => handleDeactivate(plan.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                        {plan.description && <p className="text-sm text-muted-foreground mb-3">{plan.description}</p>}
                        <div className="text-3xl font-mono font-bold text-white mb-4">
                          {plan.discountPct > 0 ? (
                            <>
                              <span className="line-through text-white/40 text-xl">${plan.priceMonthly}</span>{" "}
                              ${(plan.priceMonthly * (1 - plan.discountPct / 100)).toFixed(2)}
                            </>
                          ) : (
                            <>${plan.priceMonthly}</>
                          )}
                          <span className="text-sm text-muted-foreground font-normal">/mo</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-primary" />
                            <span className="text-white">{plan.privateCredits} private sessions/month</span>
                          </div>
                          {plan.privateCredits > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                              <Check className="w-4 h-4 text-primary/50" />
                              {plan.discountPct > 0 ? (
                                <span className="text-muted-foreground">
                                  <span className="line-through">${(plan.priceMonthly / plan.privateCredits).toFixed(2)}</span>
                                  {" "}
                                  <span className="text-emerald-400 font-medium">${((plan.priceMonthly / plan.privateCredits) * (1 - plan.discountPct / 100)).toFixed(2)}</span>
                                  {" per session"}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">${(plan.priceMonthly / plan.privateCredits).toFixed(2)} per session</span>
                              )}
                            </div>
                          )}
                          {plan.discountPct > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                              <Percent className="w-4 h-4 text-emerald-400" />
                              <span className="text-emerald-400 font-medium">{plan.discountPct}% off</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {inactivePlans.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-muted-foreground mb-4 uppercase tracking-wider">Inactive Plans</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {inactivePlans.map((plan: any) => (
                    <Card key={plan.id} className="glass-panel border-white/5 opacity-50 relative">
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        {confirmDeleteId === plan.id ? (
                          <>
                            <span className="text-xs text-red-400">Delete forever?</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs px-2 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                              onClick={() => handlePermanentDelete(plan.id)}
                              disabled={deletingId === plan.id}
                            >
                              {deletingId === plan.id ? "Deleting…" : "Yes, delete"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs px-2 text-muted-foreground hover:text-white hover:bg-white/10"
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={deletingId === plan.id}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-400/10"
                              onClick={() => handleReactivate(plan.id)}
                              disabled={reactivatingId === plan.id}
                            >
                              {reactivatingId === plan.id ? "Reactivating…" : "Reactivate"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs px-2 text-red-400/70 hover:text-red-400 hover:bg-red-400/10"
                              onClick={() => setConfirmDeleteId(plan.id)}
                            >
                              Delete
                            </Button>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-muted-foreground border border-white/10">
                              Inactive
                            </span>
                          </>
                        )}
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                        <div className="text-2xl font-mono font-bold text-white mb-2">
                          {plan.discountPct > 0 ? (
                            <>
                              <span className="line-through text-white/40 text-xl">${plan.priceMonthly}</span>{" "}
                              ${(plan.priceMonthly * (1 - plan.discountPct / 100)).toFixed(2)}
                            </>
                          ) : (
                            <>${plan.priceMonthly}</>
                          )}
                          <span className="text-sm text-muted-foreground font-normal">/mo</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{plan.privateCredits} sessions • {plan.discountPct || 0}% discount</p>
                        {plan.privateCredits > 0 && (
                          <p className="text-sm text-muted-foreground">
                            {plan.discountPct > 0 ? (
                              <>
                                <span className="line-through">${(plan.priceMonthly / plan.privateCredits).toFixed(2)}</span>
                                {" "}
                                <span className="text-emerald-400 font-medium">${((plan.priceMonthly / plan.privateCredits) * (1 - plan.discountPct / 100)).toFixed(2)}</span>
                                {" per session"}
                              </>
                            ) : (
                              <>${(plan.priceMonthly / plan.privateCredits).toFixed(2)} per session</>
                            )}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activePlans.length === 0 && inactivePlans.length === 0 && (
              <div className="text-center py-16">
                <Crown className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                <h3 className="text-xl font-bold text-white mb-2">No membership plans yet</h3>
                <p className="text-muted-foreground mb-6">Create your first plan to offer recurring memberships to students</p>
                <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-primary text-black font-bold">
                  <Plus className="w-4 h-4 mr-2" /> Create First Plan
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
